import { Flex, Icon, Text } from '@chakra-ui/react'
import { Pause, Play } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import useTranscriptionLogContext from '~/pages/CallAnalytics/Transcription/context/TranscriptionLogContext'
import { formatMilliseconds, tripLeadingZeroFromTime } from '~/utils/formatter'

import { toNounCasing } from '@mangovoice/core'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

interface TranscriptEntryHeaderProps {
  transcriptId: string
  personId: number
  personName: string
  beginOffsetMillis: number
  isActive: boolean
  isHovered: boolean
  isPlaying: boolean
}
const TranscriptEntryHeader = ({
  transcriptId,
  personName,
  beginOffsetMillis,
  isActive,
  isHovered,
  isPlaying,
}: TranscriptEntryHeaderProps) => {
  const [wasActive, setWasActive] = useState<boolean>()
  const logContext = useTranscriptionLogContext()

  //keep track of whether or not this component was active to control animation states
  useEffect(() => {
    if (isActive) {
      setWasActive(true)
    }

    return () => {
      setWasActive(undefined)
    }
  }, [isActive])

  //reset wasActive so animations don't trigger on subsequent rerenders from things like hover state changes
  useEffect(() => {
    if (wasActive && !isActive) {
      setWasActive(false)
    }
  }, [wasActive, isActive])

  const handleClick = () => {
    if (isActive) {
      if (isPlaying) {
        logContext?.onPause?.()
      } else {
        logContext?.onPlay?.()
      }
    } else {
      logContext?.onPlay?.(beginOffsetMillis)
    }
  }

  const jsx = useMemo(
    () => (
      <Flex flex="1" height="25px" alignItems="flex-end" gap="0.5rem">
        <Flex
          onClick={handleClick}
          css={{
            alignItems: 'center',
            cursor: 'pointer',
            minWidth: '56px',
            padding: '2px 6px',
            justifyContent: 'space-between',
            borderRadius: '4px',
            backgroundColor: isActive
              ? 'var(--chakra-colors-brand-muted)'
              : isHovered
                ? 'var(--chakra-colors-brand-subtle)'
                : 'var(--chakra-colors-bg-panel)',
          }}
        >
          <Text
            {...textStyles[textStyleKeys.Body4_Bold]}
            color={
              isActive ? 'brand.fg' : isHovered ? 'fg.default' : 'fg.muted'
            }
          >
            {tripLeadingZeroFromTime(formatMilliseconds(beginOffsetMillis))}
          </Text>
          <Icon
            color={
              isActive ? 'brand.fg' : isHovered ? 'fg.default' : 'transparent'
            }
            height="8px"
            width="7px"
          >
            {isActive && isPlaying ? (
              <Pause size="100%" />
            ) : (
              <Play size="100%" />
            )}
          </Icon>
        </Flex>
        <Text
          {...textStyles[textStyleKeys.Body4_Bold]}
          color={isHovered || isActive ? 'fg.default' : 'fg.muted'}
        >
          {toNounCasing(personName)}
        </Text>
      </Flex>
    ),
    [isActive, isHovered, transcriptId, isPlaying],
  )

  return jsx
}

export default TranscriptEntryHeader
